'use client';
import {
  Title,
  Note,
  Text,
  SourceItem,
  Icon,
  CustomTooltip,
  InputForm,
  IBadgeClaimCode,
} from '@ui/components';
import { BadgeSource, BadgeType } from '@ui/constants';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { IBadgeClaimSubmit } from './badge-claim-submit';
import { Control } from 'react-hook-form';
import { useMobile } from '@ui/hooks';

interface IBadgeClaimInfo extends IBadgeClaimSubmit {
  badgeNumber: number;
  badgeAmount: number;
  transferDate: string;
  platform: BadgeSource;
  publicLink: string;
  isAvailable?: string | boolean;
  currentTab?: string;
  currentSubTab?: string;
  isClaimExpired?: boolean;
  badgeType?: number | null;
  creatorHasCommunity?: boolean;
  gotoForum?(): void;
  serialNumber?: number;
  badgeOwned?: boolean;
  control?: Control<IBadgeClaimCode>;
  claimCodeActive?: boolean;
  isOwned?: boolean;
}

export function BadgeClaimInfoDetails({
  badgeAmount,
  badgeNumber,
  transferDate,
  platform,
  publicLink,
  isAvailable,
  gotoForum,
  currentTab,
  isClaimExpired,
  badgeType,
  creatorHasCommunity,
  serialNumber = 1,
  badgeState = 'default',
  badgeOwned,
  currentSubTab,
  control,
  claimCodeActive,
  isOwned,
}: IBadgeClaimInfo) {
  const isMobile = useMobile();

  function onPublicLink() {
    if (!publicLink) {
      return;
    }
    window.open(publicLink, '_blank');
  }

  const shouldRenderContainer = useMemo(() => {
    return (
      (isClaimExpired &&
        (badgeType === BadgeType.Engagement || badgeType === BadgeType.Community)) ||
      badgeType === BadgeType.Prize ||
      (badgeType === BadgeType.Community && creatorHasCommunity) ||
      (publicLink && !isClaimExpired)
    );
  }, [isClaimExpired, badgeType, creatorHasCommunity, publicLink]);

  return (
    <div className="mb-4 flex w-full flex-col gap-2">
      <div className="bg-input flex flex-col items-center rounded p-3">
        <Note
          type="xs"
          className={`text-[#534944] flex items-center gap-1 ${
            !isAvailable && badgeAmount === 0 && 'mb-2'
          }`}>
          {(currentSubTab === 'My Badges' && badgeState === 'collected') ||
          currentTab === 'Leaderboards'
            ? 'Badge Number'
            : currentSubTab === 'All Badges'
            ? badgeOwned || isOwned
              ? 'Owned'
              : 'Next Badge Number'
            : badgeState !== 'default'
            ? 'Badge Number'
            : 'Next Badge Number'}
          {badgeType == BadgeType.Prize && (
            <CustomTooltip
              small={true}
              message="Once the challenge concludes and the supply of challenge badges is exhausted, the total number of exclusive badges is finalized."
            />
          )}
        </Note>
        <Title type="mm">#{badgeNumber}</Title>
        {badgeType != BadgeType.Community && (
          <div className={`bg-inputActive rounded-full px-2 py-1 flex gap-2 items-center`}>
            <Note type="m" className="text-[#534944]">
              {`of ${badgeType == BadgeType.Prize ? serialNumber : badgeAmount}`}
            </Note>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <div
          className={`bg-input flex ${
            shouldRenderContainer ? 'basis-1/2' : 'w-full'
          } flex-col items-center justify-center rounded p-3`}>
          <Note type="xs" className="text-[#534944]">
            Transfer Date
          </Note>
          <Text type="m" className="font-semibold">
            {transferDate}
          </Text>
        </div>

        {shouldRenderContainer && (
          <div
            onClick={isClaimExpired ? undefined : onPublicLink}
            className={twMerge(
              'bg-input flex basis-1/2 items-center justify-between gap-2 rounded p-3',
              publicLink && !isClaimExpired
                ? 'hover:bg-inputActive active:bg-secondaryHover cursor-pointer'
                : '',
            )}>
            {isClaimExpired &&
              (badgeType === BadgeType.Engagement || badgeType === BadgeType.Community) && (
                <div className="w-full items-center text-center">
                  <Text type="m" className="font-semibold text-[#534944]">
                    Expired
                  </Text>
                </div>
              )}

            {badgeType == BadgeType.Prize && (
              <div className="w-full flex items-center justify-center">
                <Icon type="trophy" className="items-center" />
              </div>
            )}

            {badgeType == BadgeType.Community && creatorHasCommunity && (
              <div
                className="w-full flex items-center gap-2 justify-center cursor-pointer 
                active:scale-95 transition-transform duration-150 hover:bg-gray-100 rounded-md p-2"
                onClick={gotoForum}>
                <Text type="m" className="font-semibold">
                  Community
                </Text>
                <Icon type="people" />
              </div>
            )}

            {publicLink && !isClaimExpired && (
              <div
                className="w-full flex items-center justify-between gap-2 cursor-pointer"
                onClick={onPublicLink}>
                <div className="w-6" />
                <SourceItem source={platform} />
                <Icon type="link" />
              </div>
            )}
          </div>
        )}
      </div>
      {!isMobile && (
        <>
          {claimCodeActive && control && !badgeOwned && (
            <div className="flex flex-col items-center justify-center my-3">
              <Text type="m" className="my-2">
                Enter Unique Code to claim this badge
              </Text>
              <div className="md:w-50">
                <InputForm
                  label=""
                  placeholder="Qstw"
                  form={{
                    control,
                    name: 'claimCode',
                    rules: {
                      required: 'Please enter a valid claim code before collecting the badge.',
                      maxLength: {
                        value: 10,
                        message: 'Badge name should be less than 10 symbols',
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
